import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaDownload, FaMoneyBillWave, FaClock, FaUsers, FaCalendarAlt, FaTasks, FaChartBar, FaInfoCircle } from 'react-icons/fa';
import { FiArrowUp, FiArrowDown } from 'react-icons/fi';
import useAxios from '../utils/useAxios';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useSidebar } from '../context/SidebarContext';
import OrganisationSelector from '../components/OrganisationSelector';
import styles from '../styles/PrimesPage';
import { useAuth } from '../context/AuthContext';

const PrimesPage = () => {
  const axiosInstance = useAxios();
  const navigate = useNavigate();
  const { sidebarVisible } = useSidebar();
  const { user } = useAuth();

  // États pour les données
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total_prime: 0,
    total_heures_sup: 0,
    montant_total: 0,
  });

  // États pour la pagination et recherche
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current: 1, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // États pour les filtres
  const now = new Date();
  const [moisSelectionne, setMoisSelectionne] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );

  // États pour les organisations
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [currentOrganisation, setCurrentOrganisation] = useState(null);

  const moisOptions = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  const currentYear = new Date().getFullYear();

  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
    setPage(1);
    fetchData(1, orgId);
  };

  const generatePageSizeOptions = () => {
    const options = [10, 20, 30, 50];
    if (totalCount > 50 && !options.includes(totalCount)) {
      options.push(totalCount);
    }
    options.push('all');
    return options;
  };

  const fetchData = async (pageNum = 1, orgId = selectedOrganisation) => {
    if (!orgId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        mois: moisSelectionne,
        page: pageNum,
        organisation: orgId,
        page_size: pageSize === 'all' ? 1000 : pageSize,
        search: search
      });

      const res = await axiosInstance.get(`/pointage/primes-heures-combine-stats/?${params}`);
      setData(res.data.results);
      setStats(res.data.stats);
      setTotalCount(res.data.count);
      
      if (pageSize === 'all') {
        setPagination({ current: pageNum, totalPages: 1 });
      } else {
        setPagination({
          current: pageNum,
          totalPages: Math.ceil(res.data.count / pageSize),
        });
      }
    } catch (error) {
      Swal.fire('Erreur', "Impossible de charger les primes et heures sup.", 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedOrganisation) {
      fetchData(page);
    }
  }, [moisSelectionne, page, pageSize, search, selectedOrganisation]);

  const nombreEmployesUniques = useMemo(() => {
    return new Set(data.map(d => d.employe_id)).size;
  }, [data]);

  const formatMoney = (amount) => `${amount?.toLocaleString('fr-FR') || 0} FCFA`;

  const fetchAllData = async () => {
    try {
      let allData = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const params = new URLSearchParams({
          mois: moisSelectionne,
          organisation: selectedOrganisation,
          page: currentPage,
          page_size: 50
        });

        const response = await axiosInstance.get(`/pointage/primes-heures-combine-stats/?${params}`);
        allData = [...allData, ...response.data.results];
        hasMore = currentPage * 50 < response.data.count;
        currentPage++;
      }
      return allData;
    } catch (err) {
      console.error("Erreur lors de la récupération de toutes les données", err);
      return [];
    }
  };

  const handleExportPDF = async () => {
    setLoading(true);
    try {
      const allData = await fetchAllData();
      
      const doc = new jsPDF('p', 'mm', 'a4');
      doc.setFontSize(18);
      doc.setTextColor(27, 1, 155);
      doc.text(`Primes & Heures Supplémentaires - ${currentOrganisation?.nom || ''}`, 14, 20);

      // Période
      doc.setFontSize(12);
      doc.setTextColor(0);
      const moisTexte = new Date(moisSelectionne + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      doc.text(`Période: ${moisTexte}`, 14, 28);

      // Résumé dans l'en-tête
      const totalPrime = allData.reduce((sum, row) => sum + row.montant_prime, 0);
      const totalHeuresSup = allData.reduce((sum, row) => sum + row.heures_supplementaires, 0);
      const totalMontantHeuresSup = allData.reduce((sum, row) => sum + row.montant_heures_supplementaires, 0);
      const totalGeneral = allData.reduce((sum, row) => sum + row.total, 0);

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Récapitulatif:`, 14, 36);
      doc.text(`• Total primes ponctualité: ${formatMoney(totalPrime)}`, 20, 42);
      // CORRECTION : afficher le montant en FCFA
      doc.text(`• Total heures sup: ${formatMoney(totalMontantHeuresSup)}`, 20, 48);
      doc.text(`• Total général: ${formatMoney(totalGeneral)}`, 20, 54);

      const tableColumn = [
        'Nom',
        'Prénom',
        'Prime Ponctualité',
        'Heures Sup',
        'Montant Heures Sup',
        'Total (FCFA)',
      ];

      const tableRows = allData.map(row => [
        row.nom,
        row.prenom,
        formatMoney(row.montant_prime),
        `${row.heures_supplementaires.toFixed(2)}h`,
        formatMoney(row.montant_heures_supplementaires),
        formatMoney(row.total),
      ]);

      // Ligne des totaux
      const footerRow = [
        'TOTAUX',
        '',
        formatMoney(totalPrime),
        `${totalHeuresSup.toFixed(2)}h`,
        formatMoney(totalMontantHeuresSup),
        formatMoney(totalGeneral),
      ];

      autoTable(doc, {
        startY: 60,
        head: [tableColumn],
        body: tableRows,
        foot: [footerRow],
        styles: { fontSize: 8, cellPadding: 1.5 },
        headStyles: { fillColor: [27, 1, 155], textColor: 255, halign: 'center' },
        footStyles: { fillColor: [230, 230, 230], halign: 'center', textColor: 0, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 25 },
          4: { cellWidth: 30 },
          5: { cellWidth: 30 }
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        tableLineColor: [200, 200, 200],
        tableLineWidth: 0.2
      });

      const fileName = `primes_heures_${moisSelectionne}_${currentOrganisation?.nom || ''}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error("Erreur lors de la génération du PDF", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = newPage => {
    if (newPage > 0 && newPage <= pagination.totalPages && newPage !== page) {
      setPage(newPage);
    }
  };

  // Styles personnalisés pour les cartes
  const cardBaseStyle = {
    background: 'white',
    borderRadius: '15px',
    padding: '20px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    border: '1px solid #e0e0e0',
  };

  const cardHoverStyle = {
    transform: 'translateY(-5px)',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
  };

  return (
    <div style={{...styles.container, marginLeft: sidebarVisible ? 250 : 0}}>
      <div className="text-center mb-3">
        <h3 style={styles.header}>Primes & Heures Supplémentaires</h3>
        <hr style={styles.hr} />
      </div>

      {/* Header avec sélecteur d'organisation */}
      <div style={styles.organisationHeader}>
        <OrganisationSelector 
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />

        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Primes et heures supplémentaires pour cette organisation
            </span>
          </div>
        )}
      </div>

      {currentOrganisation ? (
        <>
          <h1 style={{ color: "#1B019B", fontSize: "28px", fontWeight: "bold", marginBottom: "20px" }}>
            📊 PRIMES & HEURES SUP - {currentOrganisation.nom}
          </h1>

          {/* Information récapitulative - CORRIGÉ */}
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            borderRadius: '10px', 
            padding: '15px 20px', 
            marginBottom: '25px',
            border: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '25px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaInfoCircle style={{ color: '#1B019B', fontSize: '20px' }} />
              <span style={{ fontWeight: '600', color: '#1B019B' }}>Récapitulatif du mois :</span>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaMoneyBillWave style={{ color: '#28a745' }} />
                <span style={{ color: '#28a745', fontWeight: '600' }}>Primes:</span>
                <span style={{ fontWeight: '500' }}>{formatMoney(stats.total_prime)}</span>
              </div>
              
              <div style={{ width: '1px', height: '25px', backgroundColor: '#ddd' }}></div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaClock style={{ color: '#0288d1' }} />
                <span style={{ color: '#0288d1', fontWeight: '600' }}>Heures sup:</span>
                {/* CORRECTION : afficher en FCFA au lieu de heures */}
                <span style={{ fontWeight: '500' }}>{formatMoney(stats.total_heures_sup)}</span>
              </div>
              
              <div style={{ width: '1px', height: '25px', backgroundColor: '#ddd' }}></div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: '600' }}>Total:</span>
                <span style={{ fontWeight: '700', color: '#1B019B' }}>{formatMoney(stats.montant_total)}</span>
              </div>
            </div>
          </div>

          {/* Cartes de statistiques redesigned - CORRIGÉ */}
          <div className="row mb-4">
            {/* Carte 1: Total Primes */}
            <div className="col-md-3 mb-3">
              <div 
                style={cardBaseStyle}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={{ 
                  backgroundColor: '#e6f7ff',
                  borderRadius: '50%',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '15px'
                }}>
                  <FaMoneyBillWave size={30} color="#1890ff" />
                </div>
                <h5 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: '700', 
                  color: '#1890ff',
                  marginBottom: '5px'
                }}>
                  {formatMoney(stats.total_prime)}
                </h5>
                <p style={{ 
                  color: '#666', 
                  fontSize: '0.9rem',
                  marginBottom: '0',
                  fontWeight: '500'
                }}>
                  Total Primes ponctualité
                </p>
              </div>
            </div>

            {/* Carte 2: Montant Heures Supplémentaires - CORRIGÉ */}
            <div className="col-md-3 mb-3">
              <div 
                style={cardBaseStyle}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={{ 
                  backgroundColor: '#f6ffed',
                  borderRadius: '50%',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '15px'
                }}>
                  <FaMoneyBillWave size={30} color="#52c41a" />
                </div>
                <h5 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: '700', 
                  color: '#52c41a',
                  marginBottom: '5px'
                }}>
                  {formatMoney(stats.total_heures_sup)}
                </h5>
                <p style={{ 
                  color: '#666', 
                  fontSize: '0.9rem',
                  marginBottom: '0',
                  fontWeight: '500'
                }}>
                  Montant Heures sup
                </p>
              </div>
            </div>

            {/* Carte 3: Employés concernés */}
            <div className="col-md-3 mb-3">
              <div 
                style={cardBaseStyle}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={{ 
                  backgroundColor: '#fff7e6',
                  borderRadius: '50%',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '15px'
                }}>
                  <FaUsers size={30} color="#fa8c16" />
                </div>
                <h5 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: '700', 
                  color: '#fa8c16',
                  marginBottom: '5px'
                }}>
                  {nombreEmployesUniques}
                </h5>
                <p style={{ 
                  color: '#666', 
                  fontSize: '0.9rem',
                  marginBottom: '0',
                  fontWeight: '500'
                }}>
                  Employés concernés
                </p>
              </div>
            </div>

            {/* Carte 4: Période */}
            <div className="col-md-3 mb-3">
              <div 
                style={cardBaseStyle}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={{ 
                  backgroundColor: '#f9f0ff',
                  borderRadius: '50%',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '15px'
                }}>
                  <FaCalendarAlt size={30} color="#722ed1" />
                </div>
                <h5 style={{ 
                  fontSize: '1.2rem', 
                  fontWeight: '700', 
                  color: '#722ed1',
                  marginBottom: '5px'
                }}>
                  {new Date(moisSelectionne + '-01').toLocaleDateString('fr-FR', { month: 'long' })}
                </h5>
                <p style={{ 
                  color: '#666', 
                  fontSize: '0.9rem',
                  marginBottom: '0',
                  fontWeight: '500'
                }}>
                  {new Date(moisSelectionne + '-01').toLocaleDateString('fr-FR', { year: 'numeric' })}
                </p>
              </div>
            </div>
          </div>

          {/* Barre de recherche et filtres */}
          <div className="d-flex flex-wrap align-items-center mb-4" style={{ gap: '15px' }}>
            {/* Sélecteur de mois */}
            <div className="d-flex align-items-center">
              <FaCalendarAlt className="me-2" style={{ color: '#1B019B' }} />
              <select
                value={moisSelectionne}
                onChange={e => { setPage(1); setMoisSelectionne(e.target.value); }}
                style={styles.select}
              >
                {moisOptions.map((label, index) => {
                  const value = String(index + 1).padStart(2, '0');
                  return <option key={value} value={`${currentYear}-${value}`}>{label} {currentYear}</option>;
                })}
              </select>
            </div>

            {/* Recherche */}
            <div className="d-flex align-items-center flex-grow-1">
              <input
                type="text"
                placeholder="Rechercher nom ou prénom..."
                style={styles.searchBar}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Sélecteur de page size */}
            <div>
              <label style={{ color: '#1B019B', fontWeight: 500 }}>Afficher :</label>
              <select 
                style={styles.select} 
                value={pageSize} 
                onChange={(e) => {
                  setPageSize(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
                  setPage(1);
                }}
                disabled={loading}
              >
                {generatePageSizeOptions().map((size, idx) => (
                  <option key={idx} value={size}>
                    {size === 'all' ? 'Tous afficher' : `${size} lignes`}
                  </option>
                ))}
              </select>
            </div>

            {/* Bouton PDF */}
            {(user?.is_super_admin || user?.permissions?.includes("EXPORT_PDF")) && (
              <button 
                className="btn btn-danger" 
                onClick={handleExportPDF} 
                disabled={loading} 
                style={{ minWidth: '160px', padding: '8px 14px', fontSize: '1rem' }}
              >
                <FaDownload className="me-2" /> 
                {loading ? 'Génération...' : 'Exporter PDF'}
              </button>
            )}
          </div>

          {/* Tableau */}
          <div className="card shadow-sm mb-4" style={{ borderRadius: '15px', border: 'none' }}>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover text-center align-middle m-0">
                <thead>
                  <tr style={styles.tableHeader}>
                    <th>Nom</th>
                    <th>Prénom</th>
                    <th>Ponctualité</th>
                    <th>Heures</th>
                    <th>Heures</th>
                    <th>Total (FCFA)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length ? (
                    data.map(row => (
                      <tr key={row.employe_id} className="table-row-hover">
                        <td>{row.nom}</td>
                        <td>{row.prenom}</td>
                        <td className="text-success fw-bold">{formatMoney(row.montant_prime)}</td>
                        <td className="text-info fw-bold">{row.heures_supplementaires.toFixed(2)}h</td>
                        <td className="text-success">{formatMoney(row.montant_heures_supplementaires)}</td>
                        <td className="text-success fw-bold" style={{ color: '#1B019B' }}>{formatMoney(row.total)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-muted py-4">
                        {loading ? 'Chargement en cours...' : 'Aucune donnée pour cette période.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="card-footer d-flex justify-content-center mt-3 bg-transparent border-top-0">
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(1)}
                        disabled={loading}
                        style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                      >
                        Première
                      </button>
                    </li>
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={loading}
                        style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                      >
                        &laquo;
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      return (
                        <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            style={page === pageNum ? { ...styles.paginationButton, ...styles.paginationActive } : styles.paginationButton}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}

                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={loading}
                        style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                      >
                        &raquo;
                      </button>
                    </li>
                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={loading}
                        style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                      >
                        Dernière
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">Sélectionnez une organisation pour commencer</p>
        </div>
      )}
    </div>
  );
};

export default PrimesPage;